import React, {
    Fragment,
    useEffect,
    useMemo,
    useRef,
    useState
} from "react";

import axios from "axios";
import { QRCodeCanvas } from "qrcode.react";
import { useTranslation } from "react-i18next";
import { GlobalSet } from "../../global/GlobalProvider";


/* =========================================================
   CONFIG
========================================================= */

const API_KEY =
    "eyJ0eXAiOiJKV1Qidfg%!#%$%LCdf#*&*JhbGciOiJSUzI1NiJ9";

const REGISTER_URL =
    "https://account.holytrust.org/register";

const PER_PAGE = 10;


/* =========================================================
   HELPERS
========================================================= */

const numberFormat = (value) => {

    const number = Number(value || 0);

    if (!Number.isFinite(number)) {
        return "0";
    }

    return new Intl.NumberFormat("en-BD", {
        minimumFractionDigits: 0,
        maximumFractionDigits: 2,
    }).format(number);
};


const money = (
    value,
    symbol = "৳"
) => {

    return `${symbol}${numberFormat(value)}`;
};


const safeText = (
    value,
    fallback = "—"
) => {

    if (
        value === null ||
        value === undefined ||
        value === ""
    ) {
        return fallback;
    }

    return value;
};


const formatDate = (value) => {

    if (!value) {
        return "—";
    }

    try {

        const date =
            new Date(
                String(value).replace(
                    " ",
                    "T"
                )
            );


        if (
            Number.isNaN(
                date.getTime()
            )
        ) {
            return value;
        }


        return date.toLocaleDateString(
            "en-BD",
            {
                year: "numeric",
                month: "short",
                day: "2-digit"
            }
        );

    } catch (error) {

        return value;
    }
};


/* =========================================================
   COMPONENT
========================================================= */

const BasicDatatable = () => {

    const { t } =
        useTranslation();

    const { CurrencySymbol } =
        GlobalSet();


    /* =====================================================
       STATE
    ===================================================== */

    const [income, setIncome] =
        useState([]);

    const [myData, setMyData] =
        useState({});

    const [loading, setLoading] =
        useState(true);

    const [pageError, setPageError] =
        useState("");

    const [search, setSearch] =
        useState("");

    const [currentPage, setCurrentPage] =
        useState(1);

    const [copied, setCopied] =
        useState("");

    const qrRef =
        useRef(null);


    /* =====================================================
       CURRENCY
    ===================================================== */

    const currencySymbol ="৳";


    /* =====================================================
       REFERRAL INFORMATION
    ===================================================== */

    const referralCode =
        myData?.reference_code || "";


    const referralLink =
        referralCode
            ? `${REGISTER_URL}?ref=${encodeURIComponent(
                referralCode
            )}`
            : REGISTER_URL;


    /* =====================================================
       LOAD API
    ===================================================== */

    const fetchSponsorData =
        async () => {

            const authToken =
                localStorage.getItem(
                    "auth_token"
                );


            if (!authToken) {

                setLoading(false);

                setPageError(
                    "Your login session was not found. Please login again."
                );

                return;
            }


            try {

                setLoading(true);

                setPageError("");


                const response =
                    await axios.post(
                        "/apps-my-sponsor",
                        {
                            key_s:
                                API_KEY
                        }
                    );


                const result =
                    response?.data || {};


                setIncome(
                    Array.isArray(
                        result.data
                    )
                        ? result.data
                        : []
                );


                setMyData(
                    result.mydata || {}
                );


            } catch (error) {

                console.error(
                    "Affiliate Data Error:",
                    error
                );


                setIncome([]);

                setMyData({});


                setPageError(
                    error?.response?.data
                        ?.message ||
                    "Unable to load affiliate information."
                );


            } finally {

                setLoading(false);
            }
        };


    useEffect(() => {

        fetchSponsorData();

    }, []);


    /* =====================================================
       COPY
    ===================================================== */

    const copyText =
        async (
            text,
            type
        ) => {

            if (!text) {
                return;
            }


            try {

                await navigator.clipboard.writeText(
                    text
                );


                setCopied(type);


                window.setTimeout(
                    () => {

                        setCopied("");

                    },
                    2500
                );


            } catch (error) {

                console.error(
                    "Copy Error:",
                    error
                );
            }
        };


    /* =====================================================
       DOWNLOAD QR
    ===================================================== */

    const downloadQRCode = () => {

        if (!qrRef.current) {
            return;
        }


        const canvas =
            qrRef.current.querySelector(
                "canvas"
            );


        if (!canvas) {
            return;
        }


        const image =
            canvas.toDataURL(
                "image/png"
            );


        const anchor =
            document.createElement(
                "a"
            );


        anchor.href =
            image;

        anchor.download =
            `referral-${referralCode || "qr"}.png`;


        document.body.appendChild(
            anchor
        );


        anchor.click();


        document.body.removeChild(
            anchor
        );
    };


    /* =====================================================
       SHARE
    ===================================================== */

    const encodedReferral =
        encodeURIComponent(
            referralLink
        );


    const shareMessage =
        encodeURIComponent(
            `Please register here: ${referralLink}`
        );


    const socialLinks = {

        whatsapp:
            `https://api.whatsapp.com/send?text=${shareMessage}`,

        facebook:
            `https://www.facebook.com/sharer/sharer.php?u=${encodedReferral}`,

        twitter:
            `https://twitter.com/intent/tweet?text=${shareMessage}`,

        telegram:
            `https://t.me/share/url?url=${encodedReferral}&text=${encodeURIComponent(
                "Please register here"
            )}`,

        linkedin:
            `https://www.linkedin.com/sharing/share-offsite/?url=${encodedReferral}`

    };


    /* =====================================================
       SEARCH
    ===================================================== */

    const filteredSponsors =
        useMemo(() => {

            const keyword =
                search
                    .trim()
                    .toLowerCase();


            if (!keyword) {

                return income;
            }


            return income.filter(
                (item) => {

                    const searchable = [

                        item.id,

                        item.name,

                        item.email,

                        item.total_invest,

                        item.team_and_spon_sell,

                        item.team_sell,

                        item.created_at

                    ]
                        .filter(
                            value =>
                                value !==
                                undefined &&
                                value !==
                                null
                        )
                        .join(" ")
                        .toLowerCase();


                    return searchable.includes(
                        keyword
                    );
                }
            );

        }, [
            income,
            search
        ]);


    useEffect(() => {

        setCurrentPage(1);

    }, [search]);


    /* =====================================================
       PAGINATION
    ===================================================== */

    const totalPages =
        Math.max(
            1,
            Math.ceil(
                filteredSponsors.length /
                PER_PAGE
            )
        );


    const currentSponsors =
        useMemo(() => {

            const start =
                (
                    currentPage -
                    1
                ) *
                PER_PAGE;


            return filteredSponsors.slice(
                start,
                start + PER_PAGE
            );

        }, [
            filteredSponsors,
            currentPage
        ]);


    const changePage =
        (page) => {

            const finalPage =
                Math.max(
                    1,
                    Math.min(
                        page,
                        totalPages
                    )
                );


            setCurrentPage(
                finalPage
            );
        };


    const pageNumbers =
        useMemo(() => {

            let start =
                Math.max(
                    1,
                    currentPage - 2
                );


            let end =
                Math.min(
                    totalPages,
                    currentPage + 2
                );


            if (
                end - start < 4
            ) {

                if (
                    start === 1
                ) {

                    end =
                        Math.min(
                            totalPages,
                            5
                        );

                } else {

                    start =
                        Math.max(
                            1,
                            totalPages - 4
                        );
                }
            }


            const pages = [];


            for (
                let i = start;
                i <= end;
                i++
            ) {

                pages.push(i);
            }


            return pages;

        }, [
            currentPage,
            totalPages
        ]);


    /* =====================================================
       SUMMARY
    ===================================================== */

    const summary =
        useMemo(() => {

            let selfSell = 0;
            let teamSell = 0;
            let selfTeam = 0;


            income.forEach(
                item => {

                    selfSell +=
                        Number(
                            item?.total_invest ||
                            0
                        );


                    teamSell +=
                        Number(
                            item?.team_sell ||
                            0
                        );


                    selfTeam +=
                        Number(
                            item?.team_and_spon_sell ||
                            0
                        );
                }
            );


            return {

                users:
                    Number(
                        myData?.count_active_spons ||
                        income.length ||
                        0
                    ),

                teamSell:
                    Number(
                        myData?.team_sell ||
                        teamSell ||
                        0
                    ),

                selfSell,

                selfTeam

            };

        }, [
            income,
            myData
        ]);


    const showingFrom =
        filteredSponsors.length === 0
            ? 0
            : (
                (
                    currentPage -
                    1
                ) *
                PER_PAGE
            ) + 1;


    const showingTo =
        Math.min(
            currentPage *
            PER_PAGE,
            filteredSponsors.length
        );


    /* =====================================================
       RENDER
    ===================================================== */

    return (

        <Fragment>


            {/* =================================================
                DESIGN
            ================================================= */}

            <style>{`

                * {
                    box-sizing: border-box;
                }


                .affiliate-page {

                    --primary: #5b4df7;
                    --primary-dark: #4738e8;
                    --primary-soft: #f1efff;

                    --green: #12b76a;
                    --green-dark: #067647;
                    --green-soft: #ecfdf3;

                    --blue: #1570ef;
                    --blue-soft: #eff8ff;

                    --orange: #f79009;
                    --orange-dark: #b54708;
                    --orange-soft: #fff7ed;

                    --text: #101828;
                    --text2: #344054;
                    --muted: #667085;

                    --border: #e4e7ec;
                    --light: #f9fafb;

                    width: 100%;

                    padding: 20px;

                    border-radius: 20px;

                    background: #ffffff;

                    color: var(--text);

                    font-family:
                        Inter,
                        -apple-system,
                        BlinkMacSystemFont,
                        "Segoe UI",
                        Arial,
                        sans-serif;
                }


                .affiliate-container {

                    width: 100%;

                    max-width: 1450px;

                    margin: 0 auto;
                }


                /* =============================================
                   HEADER
                ============================================= */

                .affiliate-header {

                    display: flex;

                    align-items: center;

                    justify-content:
                        space-between;

                    gap: 20px;

                    margin-bottom: 22px;
                }


                .affiliate-title {

                    display: flex;

                    align-items: center;

                    gap: 14px;
                }


                .affiliate-title-icon {

                    width: 52px;
                    height: 52px;

                    display: flex;

                    align-items: center;

                    justify-content: center;

                    flex-shrink: 0;

                    border-radius: 15px;

                    background:
                        var(--primary-soft);

                    color:
                        var(--primary);

                    font-size: 21px;
                }


                .affiliate-title h2 {

                    margin:
                        0 0 5px;

                    color:
                        var(--text);

                    font-size: 27px;

                    font-weight: 800;
                }


                .affiliate-title p {

                    margin: 0;

                    color:
                        var(--muted);

                    font-size: 13px;
                }


                .affiliate-code-badge {

                    display: flex;

                    align-items: center;

                    gap: 9px;

                    padding:
                        10px 15px;

                    border:
                        1px solid
                        #d9d6fe;

                    border-radius: 11px;

                    background:
                        var(--primary-soft);

                    color:
                        var(--primary);

                    font-size: 12px;

                    font-weight: 800;
                }


                /* =============================================
                   REFERRAL SECTION
                ============================================= */

                .affiliate-referral-grid {

                    display: grid;

                    grid-template-columns:
                        minmax(0, 1.65fr)
                        minmax(270px, .7fr);

                    gap: 16px;

                    margin-bottom: 16px;
                }


                .affiliate-card {

                    border:
                        1px solid
                        var(--border);

                    border-radius: 17px;

                    background: #ffffff;

                    box-shadow:
                        0 6px 24px
                        rgba(
                            16,
                            24,
                            40,
                            .05
                        );
                }


                .affiliate-card-body {

                    padding: 21px;
                }


                .affiliate-section-head {

                    display: flex;

                    align-items:
                        flex-start;

                    justify-content:
                        space-between;

                    gap: 15px;

                    margin-bottom: 20px;
                }


                .affiliate-section-head h3 {

                    margin:
                        0 0 4px;

                    color:
                        var(--text);

                    font-size: 17px;

                    font-weight: 800;
                }


                .affiliate-section-head p {

                    margin: 0;

                    color:
                        var(--muted);

                    font-size: 12px;

                    line-height: 1.5;
                }


                /* =============================================
                   LINK BOX
                ============================================= */

                .affiliate-field {

                    margin-bottom: 17px;
                }


                .affiliate-field:last-child {

                    margin-bottom: 0;
                }


                .affiliate-label {

                    display: block;

                    margin-bottom: 7px;

                    color:
                        var(--text2);

                    font-size: 12px;

                    font-weight: 700;
                }


                .affiliate-copy-box {

                    display: flex;

                    align-items: center;

                    width: 100%;

                    min-height: 50px;

                    overflow: hidden;

                    border:
                        1px solid
                        var(--border);

                    border-radius: 11px;

                    background:
                        var(--light);
                }


                .affiliate-copy-value {

                    flex: 1;

                    min-width: 0;

                    padding:
                        0 15px;

                    overflow: hidden;

                    color:
                        var(--text2);

                    font-size: 13px;

                    text-overflow:
                        ellipsis;

                    white-space: nowrap;
                }


                .affiliate-copy-button {

                    min-width: 105px;

                    height: 48px;

                    padding:
                        0 16px;

                    border: 0;

                    border-left:
                        1px solid
                        var(--border);

                    background:
                        #ffffff;

                    color:
                        var(--primary);

                    font-size: 12px;

                    font-weight: 800;

                    cursor: pointer;

                    transition:
                        .15s ease;
                }


                .affiliate-copy-button:hover {

                    background:
                        var(--primary-soft);
                }


                .affiliate-copy-button.copied {

                    color:
                        var(--green-dark);

                    background:
                        var(--green-soft);
                }


                /* =============================================
                   SOCIAL
                ============================================= */

                .affiliate-share-section {

                    margin-top: 21px;

                    padding-top: 18px;

                    border-top:
                        1px solid
                        #eef0f4;
                }


                .affiliate-share-label {

                    display: block;

                    margin-bottom: 11px;

                    color:
                        var(--muted);

                    font-size: 11px;

                    font-weight: 700;

                    text-transform:
                        uppercase;

                    letter-spacing:
                        .4px;
                }


                .affiliate-social-list {

                    display: flex;

                    align-items: center;

                    flex-wrap: wrap;

                    gap: 9px;
                }


                .affiliate-social {

                    width: 41px;
                    height: 41px;

                    display: inline-flex;

                    align-items: center;

                    justify-content: center;

                    border:
                        1px solid
                        var(--border);

                    border-radius: 11px;

                    background: #ffffff;

                    color:
                        var(--text2);

                    font-size: 17px;

                    text-decoration: none;

                    transition:
                        .18s ease;
                }


                .affiliate-social:hover {

                    transform:
                        translateY(-2px);

                    text-decoration: none;
                }


                .affiliate-social.whatsapp {

                    color: #128c7e;
                }


                .affiliate-social.facebook {

                    color: #1877f2;
                }


                .affiliate-social.twitter {

                    color: #111827;
                }


                .affiliate-social.telegram {

                    color: #229ed9;
                }


                .affiliate-social.linkedin {

                    color: #0a66c2;
                }


                /* =============================================
                   QR
                ============================================= */

                .affiliate-qr-card {

                    display: flex;

                    flex-direction: column;

                    height: 100%;
                }


                .affiliate-qr-body {

                    display: flex;

                    flex: 1;

                    align-items: center;

                    justify-content: center;

                    flex-direction: column;

                    padding: 22px;

                    text-align: center;
                }


                .affiliate-qr-wrapper {

                    display: inline-flex;

                    padding: 15px;

                    margin-bottom: 15px;

                    border:
                        1px solid
                        var(--border);

                    border-radius: 15px;

                    background: #ffffff;

                    box-shadow:
                        0 5px 20px
                        rgba(
                            16,
                            24,
                            40,
                            .06
                        );
                }


                .affiliate-qr-title {

                    margin-bottom: 5px;

                    color:
                        var(--text);

                    font-size: 15px;

                    font-weight: 800;
                }


                .affiliate-qr-text {

                    max-width: 250px;

                    margin:
                        0 auto 15px;

                    color:
                        var(--muted);

                    font-size: 11px;

                    line-height: 1.5;
                }


                .affiliate-download-button {

                    min-height: 42px;

                    display: inline-flex;

                    align-items: center;

                    justify-content: center;

                    gap: 8px;

                    padding:
                        0 17px;

                    border: 0;

                    border-radius: 10px;

                    background:
                        var(--primary);

                    color: #ffffff;

                    font-size: 12px;

                    font-weight: 800;

                    cursor: pointer;

                    transition:
                        .18s ease;
                }


                .affiliate-download-button:hover {

                    background:
                        var(--primary-dark);

                    transform:
                        translateY(-1px);
                }


                /* =============================================
                   STATS
                ============================================= */

                .affiliate-stats {

                    display: grid;

                    grid-template-columns:
                        repeat(
                            4,
                            minmax(0, 1fr)
                        );

                    gap: 13px;

                    margin-bottom: 18px;
                }


                .affiliate-stat-card {

                    padding: 18px;

                    border:
                        1px solid
                        var(--border);

                    border-radius: 15px;

                    background: #ffffff;

                    box-shadow:
                        0 4px 18px
                        rgba(
                            16,
                            24,
                            40,
                            .035
                        );
                }


                .affiliate-stat-top {

                    display: flex;

                    align-items: center;

                    justify-content:
                        space-between;

                    gap: 10px;

                    margin-bottom: 13px;
                }


                .affiliate-stat-label {

                    color:
                        var(--muted);

                    font-size: 12px;

                    font-weight: 600;
                }


                .affiliate-stat-icon {

                    width: 39px;
                    height: 39px;

                    display: flex;

                    align-items: center;

                    justify-content: center;

                    flex-shrink: 0;

                    border-radius: 11px;

                    font-size: 16px;
                }


                .affiliate-stat-icon.purple {

                    background:
                        var(--primary-soft);

                    color:
                        var(--primary);
                }


                .affiliate-stat-icon.green {

                    background:
                        var(--green-soft);

                    color:
                        var(--green-dark);
                }


                .affiliate-stat-icon.blue {

                    background:
                        var(--blue-soft);

                    color:
                        var(--blue);
                }


                .affiliate-stat-icon.orange {

                    background:
                        var(--orange-soft);

                    color:
                        var(--orange-dark);
                }


                .affiliate-stat-value {

                    display: block;

                    color:
                        var(--text);

                    font-size: 22px;

                    font-weight: 800;

                    word-break:
                        break-word;
                }


                /* =============================================
                   SPONSOR CARD
                ============================================= */

                .affiliate-history {

                    overflow: hidden;

                    border:
                        1px solid
                        var(--border);

                    border-radius: 17px;

                    background: #ffffff;

                    box-shadow:
                        0 6px 24px
                        rgba(
                            16,
                            24,
                            40,
                            .045
                        );
                }


                .affiliate-history-toolbar {

                    display: flex;

                    align-items: center;

                    justify-content:
                        space-between;

                    gap: 20px;

                    padding: 19px 20px;

                    border-bottom:
                        1px solid
                        #eef0f4;
                }


                .affiliate-history-title h3 {

                    margin:
                        0 0 4px;

                    color:
                        var(--text);

                    font-size: 17px;

                    font-weight: 800;
                }


                .affiliate-history-title p {

                    margin: 0;

                    color:
                        var(--muted);

                    font-size: 12px;
                }


                /* =============================================
                   SEARCH
                ============================================= */

                .affiliate-search {

                    position: relative;

                    width: 330px;

                    max-width: 100%;
                }


                .affiliate-search-icon {

                    position: absolute;

                    top: 50%;
                    left: 14px;

                    transform:
                        translateY(-50%);

                    color: #98a2b3;

                    pointer-events: none;
                }


                .affiliate-search input {

                    width: 100%;
                    height: 44px;

                    padding:
                        0 14px
                        0 41px;

                    border:
                        1px solid
                        var(--border);

                    border-radius: 10px;

                    outline: none;

                    background:
                        var(--light);

                    color:
                        var(--text);

                    font-size: 13px;
                }


                .affiliate-search input:focus {

                    border-color:
                        var(--primary);

                    background:
                        #ffffff;

                    box-shadow:
                        0 0 0 4px
                        rgba(
                            91,
                            77,
                            247,
                            .08
                        );
                }


                /* =============================================
                   TABLE
                ============================================= */

                .affiliate-table-wrapper {

                    width: 100%;

                    overflow-x: auto;
                }


                .affiliate-table {

                    width: 100%;

                    min-width: 1050px;

                    border-collapse:
                        collapse;
                }


                .affiliate-table thead {

                    background:
                        #f9fafb;
                }


                .affiliate-table th {

                    padding:
                        14px 16px;

                    border-bottom:
                        1px solid
                        var(--border);

                    color:
                        var(--muted);

                    font-size: 11px;

                    font-weight: 800;

                    text-align: left;

                    text-transform:
                        uppercase;

                    letter-spacing:
                        .4px;

                    white-space: nowrap;
                }


                .affiliate-table td {

                    padding:
                        16px;

                    border-bottom:
                        1px solid
                        #f0f1f4;

                    color:
                        var(--text2);

                    font-size: 13px;

                    vertical-align:
                        middle;
                }


                .affiliate-table tbody tr:hover {

                    background:
                        #fafaff;
                }


                .affiliate-serial {

                    min-width: 35px;
                    height: 31px;

                    display: inline-flex;

                    align-items: center;

                    justify-content: center;

                    padding:
                        0 8px;

                    border-radius: 8px;

                    background:
                        var(--primary-soft);

                    color:
                        var(--primary);

                    font-size: 11px;

                    font-weight: 800;
                }


                .affiliate-user {

                    display: flex;

                    align-items: center;

                    gap: 10px;
                }


                .affiliate-avatar {

                    width: 36px;
                    height: 36px;

                    display: flex;

                    align-items: center;

                    justify-content: center;

                    flex-shrink: 0;

                    border-radius: 10px;

                    background:
                        var(--primary-soft);

                    color:
                        var(--primary);

                    font-size: 13px;

                    font-weight: 800;

                    text-transform:
                        uppercase;
                }


                .affiliate-user strong {

                    display: block;

                    color:
                        var(--text);

                    font-size: 13px;
                }


                .affiliate-email {

                    color:
                        var(--muted);

                    font-size: 12px;
                }


                .affiliate-money {

                    color:
                        var(--text);

                    font-weight: 700;

                    white-space: nowrap;
                }


                .affiliate-date {

                    color:
                        var(--muted);

                    font-size: 12px;

                    white-space: nowrap;
                }


                /* =============================================
                   LOADING / EMPTY
                ============================================= */

                .affiliate-message {

                    padding:
                        60px 20px;

                    text-align: center;
                }


                .affiliate-spinner {

                    width: 38px;
                    height: 38px;

                    margin:
                        0 auto 14px;

                    border:
                        4px solid
                        #ececf1;

                    border-top-color:
                        var(--primary);

                    border-radius: 50%;

                    animation:
                        affiliateSpin
                        .8s linear
                        infinite;
                }


                @keyframes affiliateSpin {

                    to {
                        transform:
                            rotate(360deg);
                    }
                }


                .affiliate-message-icon {

                    width: 55px;
                    height: 55px;

                    display: flex;

                    align-items: center;

                    justify-content: center;

                    margin:
                        0 auto 13px;

                    border-radius: 15px;

                    background:
                        var(--primary-soft);

                    color:
                        var(--primary);

                    font-size: 20px;
                }


                .affiliate-message strong {

                    display: block;

                    margin-bottom: 5px;

                    color:
                        var(--text);

                    font-size: 16px;
                }


                .affiliate-message span {

                    color:
                        var(--muted);

                    font-size: 12px;
                }


                /* =============================================
                   FOOTER
                ============================================= */

                .affiliate-table-footer {

                    display: flex;

                    align-items: center;

                    justify-content:
                        space-between;

                    gap: 15px;

                    padding:
                        16px 20px;

                    border-top:
                        1px solid
                        #eef0f4;
                }


                .affiliate-showing {

                    color:
                        var(--muted);

                    font-size: 12px;
                }


                .affiliate-showing strong {

                    color:
                        var(--text2);
                }


                .affiliate-pagination {

                    display: flex;

                    align-items: center;

                    flex-wrap: wrap;

                    gap: 6px;
                }


                .affiliate-page-btn {

                    min-width: 37px;
                    height: 37px;

                    display: inline-flex;

                    align-items: center;

                    justify-content: center;

                    padding:
                        0 10px;

                    border:
                        1px solid
                        var(--border);

                    border-radius: 9px;

                    background:
                        #ffffff;

                    color:
                        var(--text2);

                    font-size: 12px;

                    font-weight: 700;

                    cursor: pointer;
                }


                .affiliate-page-btn:hover:not(:disabled) {

                    border-color:
                        var(--primary);

                    background:
                        var(--primary-soft);

                    color:
                        var(--primary);
                }


                .affiliate-page-btn.active {

                    border-color:
                        var(--primary);

                    background:
                        var(--primary);

                    color: #ffffff;
                }


                .affiliate-page-btn:disabled {

                    opacity: .4;

                    cursor:
                        not-allowed;
                }


                /* =============================================
                   MOBILE SPONSOR
                ============================================= */

                .affiliate-mobile-list {

                    display: none;
                }


                .affiliate-mobile-card {

                    padding: 15px;

                    margin-bottom: 11px;

                    border:
                        1px solid
                        var(--border);

                    border-radius: 13px;

                    background:
                        #ffffff;
                }


                .affiliate-mobile-card:last-child {

                    margin-bottom: 0;
                }


                .affiliate-mobile-head {

                    display: flex;

                    align-items: center;

                    gap: 10px;

                    padding-bottom: 12px;

                    margin-bottom: 8px;

                    border-bottom:
                        1px solid
                        #f0f1f4;
                }


                .affiliate-mobile-info {

                    min-width: 0;
                }


                .affiliate-mobile-info strong {

                    display: block;

                    color:
                        var(--text);

                    font-size: 13px;
                }


                .affiliate-mobile-info span {

                    display: block;

                    overflow: hidden;

                    color:
                        var(--muted);

                    font-size: 11px;

                    text-overflow:
                        ellipsis;

                    white-space: nowrap;
                }


                .affiliate-mobile-row {

                    display: flex;

                    align-items: center;

                    justify-content:
                        space-between;

                    gap: 15px;

                    padding:
                        7px 0;
                }


                .affiliate-mobile-row span {

                    color:
                        var(--muted);

                    font-size: 11px;
                }


                .affiliate-mobile-row strong {

                    color:
                        var(--text2);

                    font-size: 12px;

                    text-align: right;
                }


                /* =============================================
                   RESPONSIVE
                ============================================= */

                @media (
                    max-width: 1100px
                ) {

                    .affiliate-stats {

                        grid-template-columns:
                            repeat(
                                2,
                                minmax(0,1fr)
                            );
                    }

                }


                @media (
                    max-width: 900px
                ) {

                    .affiliate-referral-grid {

                        grid-template-columns:
                            1fr;
                    }

                }


                @media (
                    max-width: 767px
                ) {

                    .affiliate-page {

                        padding: 14px;

                        border-radius: 14px;
                    }


                    .affiliate-header {

                        align-items:
                            flex-start;

                        flex-direction:
                            column;
                    }


                    .affiliate-title {

                        align-items:
                            flex-start;
                    }


                    .affiliate-title-icon {

                        width: 45px;
                        height: 45px;
                    }


                    .affiliate-title h2 {

                        font-size: 22px;
                    }


                    .affiliate-stats {

                        grid-template-columns:
                            1fr;
                    }


                    .affiliate-history-toolbar {

                        align-items:
                            stretch;

                        flex-direction:
                            column;
                    }


                    .affiliate-search {

                        width: 100%;
                    }


                    .affiliate-desktop-table {

                        display: none;
                    }


                    .affiliate-mobile-list {

                        display: block;

                        padding: 13px;

                        background:
                            #f9fafb;
                    }


                    .affiliate-table-footer {

                        align-items:
                            flex-start;

                        flex-direction:
                            column;
                    }


                    .affiliate-pagination {

                        width: 100%;

                        justify-content:
                            center;
                    }

                }


                @media (
                    max-width: 500px
                ) {

                    .affiliate-page {

                        padding: 10px;
                    }


                    .affiliate-card-body {

                        padding: 16px;
                    }


                    .affiliate-copy-box {

                        align-items:
                            stretch;

                        flex-direction:
                            column;
                    }


                    .affiliate-copy-value {

                        width: 100%;

                        padding: 14px;

                        white-space: normal;

                        word-break:
                            break-all;
                    }


                    .affiliate-copy-button {

                        width: 100%;

                        border-left: 0;

                        border-top:
                            1px solid
                            var(--border);
                    }


                    .affiliate-qr-wrapper canvas {

                        width: 180px !important;

                        height: 180px !important;
                    }

                }

            `}</style>


            {/* =================================================
                PAGE
            ================================================= */}

            <main
                className="affiliate-page"
            >

                <div
                    className="affiliate-container"
                >


                    {/* =========================================
                        HEADER
                    ========================================= */}

                    <div
                        className="affiliate-header"
                    >

                        <div
                            className="affiliate-title"
                        >

                            <div
                                className="affiliate-title-icon"
                            >
                                <i
                                    className="fa fa-users"
                                    aria-hidden="true"
                                ></i>
                            </div>


                            <div>

                                <h2>
                                    Affiliate Program
                                </h2>

                                <p>
                                    Share your referral link,
                                    grow your network and track
                                    your team performance.
                                </p>

                            </div>

                        </div>


                        {referralCode && (

                            <div
                                className="affiliate-code-badge"
                            >

                                <i
                                    className="fa fa-link"
                                    aria-hidden="true"
                                ></i>

                                ID: {referralCode}

                            </div>

                        )}

                    </div>


                    {/* =========================================
                        LOADING MAIN DATA
                    ========================================= */}

                    {loading ? (

                        <div
                            className="affiliate-card"
                        >

                            <div
                                className="affiliate-message"
                            >

                                <div
                                    className="affiliate-spinner"
                                ></div>

                                <strong>
                                    Loading affiliate data...
                                </strong>

                                <span>
                                    Please wait a moment.
                                </span>

                            </div>

                        </div>

                    ) : pageError ? (

                        <div
                            className="affiliate-card"
                        >

                            <div
                                className="affiliate-message"
                            >

                                <div
                                    className="affiliate-message-icon"
                                >
                                    !
                                </div>

                                <strong>
                                    Unable to load data
                                </strong>

                                <span>
                                    {pageError}
                                </span>

                            </div>

                        </div>

                    ) : (

                        <Fragment>


                            {/* =================================
                                REFERRAL + QR
                            ================================= */}

                            <div
                                className="affiliate-referral-grid"
                            >


                                {/* =============================
                                    REFERRAL CARD
                                ============================= */}

                                <div
                                    className="affiliate-card"
                                >

                                    <div
                                        className="affiliate-card-body"
                                    >

                                        <div
                                            className="affiliate-section-head"
                                        >

                                            <div>

                                                <h3>
                                                    Your Referral
                                                </h3>

                                                <p>
                                                    Invite new members using
                                                    your unique referral link
                                                    or referral code.
                                                </p>

                                            </div>

                                        </div>


                                        {/* REFERRAL LINK */}

                                        <div
                                            className="affiliate-field"
                                        >

                                            <label
                                                className="affiliate-label"
                                            >
                                                Referral Link
                                            </label>


                                            <div
                                                className="affiliate-copy-box"
                                            >

                                                <div
                                                    className="affiliate-copy-value"
                                                >
                                                    {referralLink}
                                                </div>


                                                <button
                                                    type="button"
                                                    className={
                                                        `affiliate-copy-button ${
                                                            copied ===
                                                            "link"
                                                                ? "copied"
                                                                : ""
                                                        }`
                                                    }
                                                    onClick={() =>
                                                        copyText(
                                                            referralLink,
                                                            "link"
                                                        )
                                                    }
                                                >

                                                    {copied ===
                                                    "link" ? (

                                                        <Fragment>

                                                            <i
                                                                className="fa fa-check"
                                                                aria-hidden="true"
                                                            ></i>
                                                            {" "}
                                                            Copied

                                                        </Fragment>

                                                    ) : (

                                                        <Fragment>

                                                            <i
                                                                className="fa fa-copy"
                                                                aria-hidden="true"
                                                            ></i>
                                                            {" "}
                                                            {t(
                                                                "pro_affi_copy_link"
                                                            ) ||
                                                                "Copy Link"}

                                                        </Fragment>
                                                    )}

                                                </button>

                                            </div>

                                        </div>


                                        {/* REFERRAL CODE */}

                                        <div
                                            className="affiliate-field"
                                        >

                                            <label
                                                className="affiliate-label"
                                            >
                                                Referral Code
                                            </label>


                                            <div
                                                className="affiliate-copy-box"
                                            >

                                                <div
                                                    className="affiliate-copy-value"
                                                >
                                                    {safeText(
                                                        referralCode
                                                    )}
                                                </div>


                                                <button
                                                    type="button"
                                                    className={
                                                        `affiliate-copy-button ${
                                                            copied ===
                                                            "code"
                                                                ? "copied"
                                                                : ""
                                                        }`
                                                    }
                                                    onClick={() =>
                                                        copyText(
                                                            referralCode,
                                                            "code"
                                                        )
                                                    }
                                                >

                                                    {copied ===
                                                    "code" ? (

                                                        <Fragment>

                                                            <i
                                                                className="fa fa-check"
                                                                aria-hidden="true"
                                                            ></i>
                                                            {" "}
                                                            Copied

                                                        </Fragment>

                                                    ) : (

                                                        <Fragment>

                                                            <i
                                                                className="fa fa-copy"
                                                                aria-hidden="true"
                                                            ></i>
                                                            {" "}
                                                            {t(
                                                                "pro_affi_copy_link_cd"
                                                            ) ||
                                                                "Copy Code"}

                                                        </Fragment>
                                                    )}

                                                </button>

                                            </div>

                                        </div>


                                        {/* SOCIAL SHARE */}

                                        <div
                                            className="affiliate-share-section"
                                        >

                                            <span
                                                className="affiliate-share-label"
                                            >
                                                Share Referral Link
                                            </span>


                                            <div
                                                className="affiliate-social-list"
                                            >


                                                {/* WHATSAPP */}

                                                <a
                                                    href={
                                                        socialLinks.whatsapp
                                                    }
                                                    target="_blank"
                                                    rel="noopener noreferrer"
                                                    className="affiliate-social whatsapp"
                                                    title="Share on WhatsApp"
                                                >
                                                    <i
                                                        className="fab fa-whatsapp"
                                                    ></i>
                                                </a>


                                                {/* FACEBOOK */}

                                                <a
                                                    href={
                                                        socialLinks.facebook
                                                    }
                                                    target="_blank"
                                                    rel="noopener noreferrer"
                                                    className="affiliate-social facebook"
                                                    title="Share on Facebook"
                                                >
                                                    <i
                                                        className="fab fa-facebook-f"
                                                    ></i>
                                                </a>


                                                {/* TWITTER */}

                                                <a
                                                    href={
                                                        socialLinks.twitter
                                                    }
                                                    target="_blank"
                                                    rel="noopener noreferrer"
                                                    className="affiliate-social twitter"
                                                    title="Share on Twitter"
                                                >
                                                    <i
                                                        className="fab fa-twitter"
                                                    ></i>
                                                </a>


                                                {/* TELEGRAM */}

                                                <a
                                                    href={
                                                        socialLinks.telegram
                                                    }
                                                    target="_blank"
                                                    rel="noopener noreferrer"
                                                    className="affiliate-social telegram"
                                                    title="Share on Telegram"
                                                >
                                                    <i
                                                        className="fab fa-telegram-plane"
                                                    ></i>
                                                </a>


                                                {/* LINKEDIN */}

                                                <a
                                                    href={
                                                        socialLinks.linkedin
                                                    }
                                                    target="_blank"
                                                    rel="noopener noreferrer"
                                                    className="affiliate-social linkedin"
                                                    title="Share on LinkedIn"
                                                >
                                                    <i
                                                        className="fab fa-linkedin-in"
                                                    ></i>
                                                </a>

                                            </div>

                                        </div>

                                    </div>

                                </div>


                                {/* =============================
                                    QR CARD
                                ============================= */}

                                <div
                                    className="affiliate-card affiliate-qr-card"
                                >

                                    <div
                                        className="affiliate-qr-body"
                                    >

                                        <div
                                            ref={qrRef}
                                            className="affiliate-qr-wrapper"
                                        >

                                            <QRCodeCanvas
                                                id="qrCode"
                                                value={
                                                    referralLink
                                                }
                                                size={205}
                                                bgColor="#ffffff"
                                                fgColor="#101828"
                                                level="H"
                                                includeMargin={false}
                                            />

                                        </div>


                                        <div
                                            className="affiliate-qr-title"
                                        >
                                            Referral QR Code
                                        </div>


                                        <p
                                            className="affiliate-qr-text"
                                        >
                                            Scan this QR code to
                                            open your registration
                                            link instantly.
                                        </p>


                                        <button
                                            type="button"
                                            className="affiliate-download-button"
                                            onClick={
                                                downloadQRCode
                                            }
                                        >

                                            <i
                                                className="fa fa-download"
                                                aria-hidden="true"
                                            ></i>

                                            {t(
                                                "pro_affi_dow_qe"
                                            ) ||
                                                "Download QR"}

                                        </button>

                                    </div>

                                </div>

                            </div>


                            {/* =================================
                                STATISTICS
                            ================================= */}

                            <div
                                className="affiliate-stats"
                            >


                                {/* ACTIVE USERS */}

                                <div
                                    className="affiliate-stat-card"
                                >

                                    <div
                                        className="affiliate-stat-top"
                                    >

                                        <span
                                            className="affiliate-stat-label"
                                        >
                                            Total Referral Users
                                        </span>


                                        <div
                                            className="affiliate-stat-icon purple"
                                        >
                                            <i
                                                className="fa fa-users"
                                                aria-hidden="true"
                                            ></i>
                                        </div>

                                    </div>


                                    <strong
                                        className="affiliate-stat-value"
                                    >
                                        {numberFormat(
                                            summary.users
                                        )}
                                    </strong>

                                </div>


                                {/* TEAM SELL */}

                                <div
                                    className="affiliate-stat-card"
                                >

                                    <div
                                        className="affiliate-stat-top"
                                    >

                                        <span
                                            className="affiliate-stat-label"
                                        >
                                            My Team Sell
                                        </span>


                                        <div
                                            className="affiliate-stat-icon green"
                                        >
                                            <i
                                                className="fa fa-line-chart"
                                                aria-hidden="true"
                                            ></i>
                                        </div>

                                    </div>


                                    <strong
                                        className="affiliate-stat-value"
                                    >
                                        {money(
                                            summary.teamSell,
                                            currencySymbol
                                        )}
                                    </strong>

                                </div>


                                {/* SELF SELL */}

                                <div
                                    className="affiliate-stat-card"
                                >

                                    <div
                                        className="affiliate-stat-top"
                                    >

                                        <span
                                            className="affiliate-stat-label"
                                        >
                                            Referral Self Sell
                                        </span>


                                        <div
                                            className="affiliate-stat-icon blue"
                                        >
                                            <i
                                                className="fa fa-user"
                                                aria-hidden="true"
                                            ></i>
                                        </div>

                                    </div>


                                    <strong
                                        className="affiliate-stat-value"
                                    >
                                        {money(
                                            summary.selfSell,
                                            currencySymbol
                                        )}
                                    </strong>

                                </div>


                                {/* SELF + TEAM */}

                                <div
                                    className="affiliate-stat-card"
                                >

                                    <div
                                        className="affiliate-stat-top"
                                    >

                                        <span
                                            className="affiliate-stat-label"
                                        >
                                            Self & Team Sell
                                        </span>


                                        <div
                                            className="affiliate-stat-icon orange"
                                        >
                                            <i
                                                className="fa fa-sitemap"
                                                aria-hidden="true"
                                            ></i>
                                        </div>

                                    </div>


                                    <strong
                                        className="affiliate-stat-value"
                                    >
                                        {money(
                                            summary.selfTeam,
                                            currencySymbol
                                        )}
                                    </strong>

                                </div>

                            </div>


                            {/* =================================
                                SPONSOR HISTORY
                            ================================= */}

                            <div
                                className="affiliate-history"
                            >


                                {/* HEADER */}

                                <div
                                    className="affiliate-history-toolbar"
                                >

                                    <div
                                        className="affiliate-history-title"
                                    >

                                        <h3>
                                            {t(
                                                "pro_affi_sp_his"
                                            ) ||
                                                "Sponsor History"}
                                        </h3>

                                        <p>
                                            View your direct referral
                                            members and their sales
                                            performance.
                                        </p>

                                    </div>


                                    <div
                                        className="affiliate-search"
                                    >

                                        <span
                                            className="affiliate-search-icon"
                                        >
                                            <i
                                                className="fa fa-search"
                                                aria-hidden="true"
                                            ></i>
                                        </span>


                                        <input
                                            type="text"
                                            value={
                                                search
                                            }
                                            placeholder="Search name, email..."
                                            onChange={
                                                e =>
                                                    setSearch(
                                                        e.target.value
                                                    )
                                            }
                                        />

                                    </div>

                                </div>


                                {/* =================================
                                    EMPTY
                                ================================= */}

                                {currentSponsors.length ===
                                0 ? (

                                    <div
                                        className="affiliate-message"
                                    >

                                        <div
                                            className="affiliate-message-icon"
                                        >
                                            <i
                                                className="fa fa-users"
                                                aria-hidden="true"
                                            ></i>
                                        </div>


                                        <strong>
                                            No sponsor records found
                                        </strong>


                                        <span>
                                            {search
                                                ? "No records match your search."
                                                : "Your referral members will appear here."
                                            }
                                        </span>

                                    </div>

                                ) : (

                                    <Fragment>


                                        {/* =====================
                                            DESKTOP TABLE
                                        ===================== */}

                                        <div
                                            className="affiliate-desktop-table"
                                        >

                                            <div
                                                className="affiliate-table-wrapper"
                                            >

                                                <table
                                                    className="affiliate-table"
                                                >

                                                    <thead>

                                                        <tr>

                                                            <th>
                                                                SL
                                                            </th>

                                                            <th>
                                                                {t(
                                                                    "pro_affi_name"
                                                                ) ||
                                                                    "Name"}
                                                            </th>

                                                            <th>
                                                                Email
                                                            </th>

                                                            <th>
                                                                Self Sell
                                                            </th>

                                                            <th>
                                                                Self & Team
                                                            </th>

                                                            <th>
                                                                Team Sell
                                                            </th>

                                                            <th>
                                                                {t(
                                                                    "pro_affi_date"
                                                                ) ||
                                                                    "Date"}
                                                            </th>

                                                        </tr>

                                                    </thead>


                                                    <tbody>

                                                        {currentSponsors.map(
                                                            (
                                                                row,
                                                                index
                                                            ) => {

                                                                const serial =
                                                                    (
                                                                        (
                                                                            currentPage -
                                                                            1
                                                                        ) *
                                                                        PER_PAGE
                                                                    ) +
                                                                    index +
                                                                    1;


                                                                const name =
                                                                    row.name ||
                                                                    "Not Verified";


                                                                const initial =
                                                                    String(
                                                                        name
                                                                    )
                                                                        .charAt(
                                                                            0
                                                                        )
                                                                        .toUpperCase();


                                                                return (

                                                                    <tr
                                                                        key={
                                                                            row.id ||
                                                                            serial
                                                                        }
                                                                    >

                                                                        <td>

                                                                            <span
                                                                                className="affiliate-serial"
                                                                            >
                                                                                {serial}
                                                                            </span>

                                                                        </td>


                                                                        <td>

                                                                            <div
                                                                                className="affiliate-user"
                                                                            >

                                                                                <div
                                                                                    className="affiliate-avatar"
                                                                                >
                                                                                    {initial}
                                                                                </div>


                                                                                <strong>
                                                                                    {name}
                                                                                </strong>

                                                                            </div>

                                                                        </td>


                                                                        <td>

                                                                            <span
                                                                                className="affiliate-email"
                                                                            >
                                                                                {safeText(
                                                                                    row.email
                                                                                )}
                                                                            </span>

                                                                        </td>


                                                                        <td>

                                                                            <span
                                                                                className="affiliate-money"
                                                                            >
                                                                                {money(
                                                                                    row.total_invest,
                                                                                    currencySymbol
                                                                                )}
                                                                            </span>

                                                                        </td>


                                                                        <td>

                                                                            <span
                                                                                className="affiliate-money"
                                                                            >
                                                                                {money(
                                                                                    row.team_and_spon_sell,
                                                                                    currencySymbol
                                                                                )}
                                                                            </span>

                                                                        </td>


                                                                        <td>

                                                                            <span
                                                                                className="affiliate-money"
                                                                            >
                                                                                {money(
                                                                                    row.team_sell,
                                                                                    currencySymbol
                                                                                )}
                                                                            </span>

                                                                        </td>


                                                                        <td>

                                                                            <span
                                                                                className="affiliate-date"
                                                                            >
                                                                                {formatDate(
                                                                                    row.created_at
                                                                                )}
                                                                            </span>

                                                                        </td>

                                                                    </tr>
                                                                );
                                                            }
                                                        )}

                                                    </tbody>

                                                </table>

                                            </div>

                                        </div>


                                        {/* =====================
                                            MOBILE
                                        ===================== */}

                                        <div
                                            className="affiliate-mobile-list"
                                        >

                                            {currentSponsors.map(
                                                (
                                                    row,
                                                    index
                                                ) => {

                                                    const serial =
                                                        (
                                                            (
                                                                currentPage -
                                                                1
                                                            ) *
                                                            PER_PAGE
                                                        ) +
                                                        index +
                                                        1;


                                                    const name =
                                                        row.name ||
                                                        "Not Verified";


                                                    const initial =
                                                        String(
                                                            name
                                                        )
                                                            .charAt(
                                                                0
                                                            )
                                                            .toUpperCase();


                                                    return (

                                                        <div
                                                            className="affiliate-mobile-card"
                                                            key={
                                                                row.id ||
                                                                serial
                                                            }
                                                        >

                                                            <div
                                                                className="affiliate-mobile-head"
                                                            >

                                                                <div
                                                                    className="affiliate-avatar"
                                                                >
                                                                    {initial}
                                                                </div>


                                                                <div
                                                                    className="affiliate-mobile-info"
                                                                >

                                                                    <strong>
                                                                        {name}
                                                                    </strong>

                                                                    <span>
                                                                        {safeText(
                                                                            row.email
                                                                        )}
                                                                    </span>

                                                                </div>

                                                            </div>


                                                            <div
                                                                className="affiliate-mobile-row"
                                                            >

                                                                <span>
                                                                    Self Sell
                                                                </span>

                                                                <strong>
                                                                    {money(
                                                                        row.total_invest,
                                                                        currencySymbol
                                                                    )}
                                                                </strong>

                                                            </div>


                                                            <div
                                                                className="affiliate-mobile-row"
                                                            >

                                                                <span>
                                                                    Self & Team
                                                                </span>

                                                                <strong>
                                                                    {money(
                                                                        row.team_and_spon_sell,
                                                                        currencySymbol
                                                                    )}
                                                                </strong>

                                                            </div>


                                                            <div
                                                                className="affiliate-mobile-row"
                                                            >

                                                                <span>
                                                                    Team Sell
                                                                </span>

                                                                <strong>
                                                                    {money(
                                                                        row.team_sell,
                                                                        currencySymbol
                                                                    )}
                                                                </strong>

                                                            </div>


                                                            <div
                                                                className="affiliate-mobile-row"
                                                            >

                                                                <span>
                                                                    Joined
                                                                </span>

                                                                <strong>
                                                                    {formatDate(
                                                                        row.created_at
                                                                    )}
                                                                </strong>

                                                            </div>

                                                        </div>
                                                    );
                                                }
                                            )}

                                        </div>

                                    </Fragment>
                                )}


                                {/* =================================
                                    PAGINATION
                                ================================= */}

                                {filteredSponsors.length >
                                    0 && (

                                    <div
                                        className="affiliate-table-footer"
                                    >

                                        <div
                                            className="affiliate-showing"
                                        >

                                            Showing{" "}

                                            <strong>
                                                {showingFrom}
                                            </strong>

                                            {" "}to{" "}

                                            <strong>
                                                {showingTo}
                                            </strong>

                                            {" "}of{" "}

                                            <strong>
                                                {
                                                    filteredSponsors
                                                        .length
                                                }
                                            </strong>

                                            {" "}entries

                                        </div>


                                        <div
                                            className="affiliate-pagination"
                                        >


                                            {/* FIRST */}

                                            <button
                                                type="button"
                                                className="affiliate-page-btn"
                                                disabled={
                                                    currentPage ===
                                                    1
                                                }
                                                onClick={() =>
                                                    changePage(
                                                        1
                                                    )
                                                }
                                            >
                                                <i
                                                    className="fa fa-angle-double-left"
                                                    aria-hidden="true"
                                                ></i>
                                            </button>


                                            {/* PREVIOUS */}

                                            <button
                                                type="button"
                                                className="affiliate-page-btn"
                                                disabled={
                                                    currentPage ===
                                                    1
                                                }
                                                onClick={() =>
                                                    changePage(
                                                        currentPage -
                                                        1
                                                    )
                                                }
                                            >
                                                <i
                                                    className="fa fa-angle-left"
                                                    aria-hidden="true"
                                                ></i>
                                            </button>


                                            {/* NUMBERS */}

                                            {pageNumbers.map(
                                                page => (

                                                    <button
                                                        type="button"
                                                        key={
                                                            page
                                                        }
                                                        className={
                                                            `affiliate-page-btn ${
                                                                currentPage ===
                                                                page
                                                                    ? "active"
                                                                    : ""
                                                            }`
                                                        }
                                                        onClick={() =>
                                                            changePage(
                                                                page
                                                            )
                                                        }
                                                    >
                                                        {page}
                                                    </button>

                                                )
                                            )}


                                            {/* NEXT */}

                                            <button
                                                type="button"
                                                className="affiliate-page-btn"
                                                disabled={
                                                    currentPage ===
                                                    totalPages
                                                }
                                                onClick={() =>
                                                    changePage(
                                                        currentPage +
                                                        1
                                                    )
                                                }
                                            >
                                                <i
                                                    className="fa fa-angle-right"
                                                    aria-hidden="true"
                                                ></i>
                                            </button>


                                            {/* LAST */}

                                            <button
                                                type="button"
                                                className="affiliate-page-btn"
                                                disabled={
                                                    currentPage ===
                                                    totalPages
                                                }
                                                onClick={() =>
                                                    changePage(
                                                        totalPages
                                                    )
                                                }
                                            >
                                                <i
                                                    className="fa fa-angle-double-right"
                                                    aria-hidden="true"
                                                ></i>
                                            </button>

                                        </div>

                                    </div>
                                )}

                            </div>

                        </Fragment>
                    )}

                </div>

            </main>

        </Fragment>
    );
};


export default BasicDatatable;